
import React from 'react';
import { Settings, Crown, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

export interface CompressionConfig {
  quality: number;
  preset: string;
  autoResize: boolean;
  maxWidth?: number;
  maxHeight?: number;
}

interface CompressionSettingsProps {
  config: CompressionConfig;
  onConfigChange: (config: CompressionConfig) => void;
  isOpen: boolean;
  onToggle: () => void;
  isPremium?: boolean;
}

const CompressionSettings: React.FC<CompressionSettingsProps> = ({
  config,
  onConfigChange,
  isOpen,
  onToggle,
  isPremium = false
}) => {
  const presets = [
    { value: 'high', label: 'Alta Calidad (0.9)', quality: 0.9 },
    { value: 'medium', label: 'Calidad Media (0.7)', quality: 0.7 },
    { value: 'low', label: 'Baja Calidad (0.5)', quality: 0.5 },
    ...(isPremium ? [
      { value: 'ultra', label: 'Ultra Calidad (0.95) - Premium', quality: 0.95 },
      { value: 'web', label: 'Web Optimizada (0.6) - Premium', quality: 0.6 }
    ] : []),
    { value: 'custom', label: 'Personalizado', quality: config.quality }
  ];

  const handlePresetChange = (preset: string) => {
    const selectedPreset = presets.find(p => p.value === preset);
    if (selectedPreset && preset !== 'custom') {
      onConfigChange({
        ...config,
        preset,
        quality: selectedPreset.quality
      });
    } else {
      onConfigChange({ ...config, preset });
    }
  };

  const handleQualityChange = (value: number[]) => {
    onConfigChange({
      ...config,
      quality: value[0] / 100,
      preset: 'custom'
    });
  };

  const handleAutoResizeChange = (checked: boolean) => {
    onConfigChange({
      ...config,
      autoResize: checked
    });
  };

  const handleMaxWidthChange = (value: string) => {
    onConfigChange({
      ...config,
      maxWidth: value !== "none" ? parseInt(value) : undefined
    });
  };

  const handleMaxHeightChange = (value: string) => {
    onConfigChange({
      ...config,
      maxHeight: value !== "none" ? parseInt(value) : undefined
    });
  };

  return (
    <div className={`mb-6 border-2 rounded-xl shadow-lg transition-all duration-300 ${
      isPremium 
        ? 'bg-gradient-to-r from-purple-50 via-white to-purple-50 border-purple-300 shadow-purple-100' 
        : 'bg-gradient-to-r from-blue-50 via-white to-blue-50 border-blue-300 shadow-blue-100'
    } ${isOpen ? 'ring-2 ring-opacity-20 ' + (isPremium ? 'ring-purple-300' : 'ring-blue-300') : ''}`}>
      
      {/* Prominent Header with Animation */}
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between p-5 transition-all duration-300 rounded-t-xl ${
          isOpen 
            ? isPremium 
              ? 'bg-gradient-to-r from-purple-100 to-purple-50 hover:from-purple-150 hover:to-purple-100' 
              : 'bg-gradient-to-r from-blue-100 to-blue-50 hover:from-blue-150 hover:to-blue-100'
            : 'hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg transition-colors ${
            isPremium ? 'bg-purple-500' : 'bg-blue-500'
          }`}>
            <Settings className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              Configuración de Compresión
              {isPremium && <Crown className="w-5 h-5 text-purple-500" />}
            </h3>
            <p className="text-sm text-gray-600">
              {isOpen ? 'Personaliza la calidad de compresión' : 'Haz clic para configurar la compresión'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {!isOpen && (
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
              isPremium 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-blue-100 text-blue-700'
            }`}>
              <Zap className="w-3 h-3" />
              Configurar
            </div>
          )}
          <div className={`p-1 rounded-full transition-all duration-300 ${
            isOpen 
              ? isPremium ? 'bg-purple-200' : 'bg-blue-200'
              : 'bg-gray-200'
          }`}>
            {isOpen ? (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </div>
        </div>
      </button>

      {/* Animated Content */}
      <div className={`transition-all duration-300 overflow-hidden ${
        isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="p-6 border-t space-y-6">
          {/* Premium Banner */}
          {isPremium && (
            <div className="bg-gradient-to-r from-purple-100 to-purple-50 border-2 border-purple-200 rounded-xl p-4 animate-fade-in">
              <div className="flex items-center gap-3 text-purple-700">
                <Crown className="w-5 h-5" />
                <span className="font-semibold">Opciones Premium Disponibles</span>
                <div className="ml-auto bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                  PREMIUM
                </div>
              </div>
            </div>
          )}

          {/* Preset Selection with Enhanced Styling */}
          <div className="space-y-3">
            <Label className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Preajustes de Calidad
            </Label>
            <Select value={config.preset} onValueChange={handlePresetChange}>
              <SelectTrigger className={`w-full h-12 text-base border-2 transition-colors ${
                isPremium ? 'border-purple-200 focus:border-purple-400' : 'border-blue-200 focus:border-blue-400'
              }`}>
                <SelectValue placeholder="Selecciona un preset" />
              </SelectTrigger>
              <SelectContent>
                {presets.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value} className="text-base py-3">
                    <div className="flex items-center gap-2">
                      {preset.label.includes('Premium') && <Crown className="w-4 h-4 text-purple-500" />}
                      {preset.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Quality Slider */}
          {config.preset === 'custom' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-xl border animate-fade-in">
              <div className="flex justify-between items-center">
                <Label className="text-base font-semibold text-gray-700">Calidad Personalizada</Label>
                <span className={`text-lg font-bold px-3 py-1 rounded-full ${
                  isPremium ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {Math.round(config.quality * 100)}%
                </span>
              </div>
              <Slider
                value={[config.quality * 100]}
                onValueChange={handleQualityChange}
                max={isPremium ? 100 : 90}
                min={10}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-500">
                <span>Menor tamaño</span>
                <span>{isPremium ? 'Ultra calidad' : 'Mayor calidad'}</span>
              </div>
              {!isPremium && config.quality > 0.9 && (
                <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded-lg">
                  💡 Hazte Premium para acceder a calidad ultra (95%+)
                </p>
              )}
            </div>
          )}

          {/* Auto Resize Option */}
          <div className="space-y-4 p-4 bg-gray-50 rounded-xl border">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-base font-semibold text-gray-700">Redimensionar automáticamente</Label>
                <p className="text-sm text-gray-600">
                  Reduce las dimensiones de imágenes muy grandes
                </p>
              </div>
              <Switch
                checked={config.autoResize}
                onCheckedChange={handleAutoResizeChange}
                className="scale-125"
              />
            </div>

            {/* Size Limits */}
            {config.autoResize && (
              <div className="grid grid-cols-2 gap-4 pt-4 border-t animate-fade-in">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Ancho máximo (px)</Label>
                  <Select
                    value={config.maxWidth?.toString() || "none"}
                    onValueChange={handleMaxWidthChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sin límite" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin límite</SelectItem>
                      <SelectItem value="1920">1920px</SelectItem>
                      <SelectItem value="1280">1280px</SelectItem>
                      <SelectItem value="800">800px</SelectItem>
                      {isPremium && (
                        <>
                          <SelectItem value="4096">4K (4096px) - Premium</SelectItem>
                          <SelectItem value="3840">4K UHD (3840px) - Premium</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Alto máximo (px)</Label>
                  <Select
                    value={config.maxHeight?.toString() || "none"}
                    onValueChange={handleMaxHeightChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sin límite" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin límite</SelectItem>
                      <SelectItem value="1080">1080px</SelectItem>
                      <SelectItem value="720">720px</SelectItem>
                      <SelectItem value="600">600px</SelectItem>
                      {isPremium && (
                        <>
                          <SelectItem value="2160">4K (2160px) - Premium</SelectItem>
                          <SelectItem value="1440">2K (1440px) - Premium</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* Quality Info */}
          <div className={`p-4 rounded-xl border-2 ${
            isPremium 
              ? 'bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200' 
              : 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200'
          }`}>
            <p className={`text-sm font-medium ${isPremium ? 'text-purple-800' : 'text-blue-800'}`}>
              <strong>💡 Recomendación:</strong> Para fotos web usa calidad media (70%), 
              para archivos de impreso usa alta calidad (90%).
              {isPremium && " Como usuario Premium, puedes usar ultra calidad (95%) para resultados excepcionales."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompressionSettings;
